/* eslint-disable no-param-reassign */
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { EMPTY_FILTER_QUERY } from "./consts";

import type { FilterQueryType } from "./types";

const name = "fileImportPayments";

type FileImportPaymentsState = {
  byId: Record<string, API.BulkPayment>;
  list: string[]; // all IDs in display order
  pageMap: Record<string, string[]>; // page number: array of IDs
  next: string | null;
  total: number;
  filters: FilterQueryType;
};

const initialState: FileImportPaymentsState = {
  byId: {},
  list: [],
  pageMap: {},
  next: null,
  total: 0,
  filters: EMPTY_FILTER_QUERY,
};

const slice = createSlice({
  name,
  initialState,
  reducers: {
    upsertPage: (
      state,
      action: PayloadAction<{
        pageNumber: number;
        results: API.BulkPayment[];
        total: number;
      }>,
    ) => {
      const { results, total, pageNumber } = action.payload;

      results.forEach((bulkPayment: API.BulkPayment) => {
        state.byId[bulkPayment.id] = bulkPayment;
      });

      state.pageMap[pageNumber] = results.map(
        (bulkPayment: API.BulkPayment) => bulkPayment.id,
      );
      state.total = total;
    },

    appendResults: (
      state,
      action: PayloadAction<{
        results: API.BulkPayment[];
        total: number;
      }>,
    ) => {
      const { results, total } = action.payload;

      const existingIds = new Set(state.list);
      results.forEach((bulkPayment: API.BulkPayment) => {
        state.byId[bulkPayment.id] = bulkPayment;
        if (!existingIds.has(bulkPayment.id)) {
          state.list.push(bulkPayment.id);
          existingIds.add(bulkPayment.id);
        }
      });
      state.total = total;
    },

    replaceResults: (
      state,
      action: PayloadAction<{
        results: API.BulkPayment[];
        total: number;
      }>,
    ) => {
      const { results, total } = action.payload;
      results.forEach((bulkPayment: API.BulkPayment) => {
        state.byId[bulkPayment.id] = bulkPayment;
      });
      state.list = results.map((bulkPayment) => bulkPayment.id);
      state.total = total;
    },

    updateFilters: (
      state,
      action: PayloadAction<Partial<FileImportPaymentsState["filters"]>>,
    ) => {
      const { payload } = action;
      state.filters = {
        ...state.filters,
        ...payload,
      };
    },

    setTotal: (state, action: PayloadAction<number>) => {
      state.total = action.payload;
    },

    resetState: (state) => {
      Object.assign(state, initialState);
    },
  },
});

export const { actions } = slice;

export default {
  [name]: slice.reducer,
};
